<template>
  <!--
    统一的头像渲染：有地址就画图片，地址为空 / 加载失败就回退成昵称首字母。

    用法（父组件直接用自己原来的头像 class 即可，尺寸、描边、底色都由父组件控制）：
      <UserAvatar class="friend-avatar" :url="friend.avatarUrl" :name="displayName(friend)" />

    关键点：
    1. 地址统一过 resolveAvatarUrl()：HTTPS 页面下的 http 图片会走后端同源代理，
       否则会被浏览器按混合内容拦掉（这就是「头像时有概率看不见」的根因）。
    2. failed 是一次性的错误态，必须在地址变化时重置，
       否则一次网络抖动就会让头像永久退回首字母（老代码的 bug）。
  -->
  <span class="user-avatar" :class="{ 'user-avatar-onair': onair }">
    <img
      v-if="src && !failed"
      class="user-avatar-img"
      :src="src"
      :alt="name || 'avatar'"
      @error="failed = true"
    >
    <span v-else class="user-avatar-text">{{ firstChar }}</span>
  </span>
</template>

<script>
import { resolveAvatarUrl, avatarFirstChar } from '@/utils/avatar.js'

export default {
  name: 'UserAvatar',
  props: {
    /** 后端返回的头像地址（avatarUrl），空值自动回退首字母 */
    url: {
      type: String,
      default: ''
    },
    /** 昵称 / 备注名，用于首字母兜底与 img alt */
    name: {
      type: String,
      default: ''
    },
    /**
     * 是否「正在做」：true 时头像外圈染主色绿并带呼吸光环，
     * 与动态行的绿点同一套语义色，用于表达「此刻正在忙」。
     */
    onair: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      failed: false
    }
  },
  computed: {
    src() {
      return resolveAvatarUrl(this.url)
    },
    firstChar() {
      return avatarFirstChar(this.name)
    }
  },
  watch: {
    // 换了头像（上传成功 / 拉到服务端最新资料）后要允许重新尝试加载，
    // 否则上一次加载失败留下的 failed=true 会让新头像也显示不出来。
    src() {
      this.failed = false
    }
  }
}
</script>

<style scoped>
.user-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  line-height: 1;
  text-align: center;
}

/* 图片铺满父组件给出的圆形框；父组件的 border-radius / border / background 保持不变 */
.user-avatar-img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  border-radius: 50%;
  background: transparent;
}

.user-avatar-text {
  line-height: 1;
  user-select: none;
}

/* ===== 正在做：头像外圈绿色光环 =====
   为什么用「外圈描边 + 呼吸光晕」而不是角标小绿点：
   1. 与昵称下方动态行的绿点共用同一套主色绿语义，一眼能对应上；
   2. 光环画在 border 外侧（box-shadow），不遮头像本体，首字母兜底时也看得清；
   3. 44px / 36px 等小尺寸下比角标更显眼，也不会被列表的圆角裁掉。

   注意选择器写成 `.user-avatar.user-avatar-onair`（特异性 0,2,0）：
   父组件往往用 `.avatar { border: 2px solid ... }` 或扁平主题的
   `.avatar, .search-avatar { border-color: ... }`（特异性 0,1,0）控制描边，
   单写 `.user-avatar-onair` 会被它们按源码顺序压过去，光环时有时无。 */
.user-avatar.user-avatar-onair {
  border-color: var(--lp-primary, #4aa46a);
  animation: user-avatar-onair 2.2s ease-in-out infinite;
}

@keyframes user-avatar-onair {
  0%, 100% { box-shadow: 0 0 0 2px rgba(74, 164, 106, 0.18); }
  50% { box-shadow: 0 0 0 5px rgba(74, 164, 106, 0.32); }
}

/* 系统减弱动效时保留静态绿圈，去掉呼吸 */
@media (prefers-reduced-motion: reduce) {
  .user-avatar.user-avatar-onair {
    animation: none;
    box-shadow: 0 0 0 3px rgba(74, 164, 106, 0.22);
  }
}
</style>
